import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  TextField,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Paper
} from "@mui/material";
import AdminLayout from "./Drawer";
import { formatDate, formatDateTime } from "../comman";
import "./userList.css";

const ScannerList = () => {
  const [events, setEvents] = useState([]);
  const [scanList, setScanList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [dailySummary, setDailySummary] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch scans only when event or date changes AND event is selected
  useEffect(() => {
    if (selectedEvent) {
      fetchScans();
    }
  }, [selectedEvent, selectedDate]);

  const fetchEvents = async () => {
    try {
      const response = await fetch("/api/get/events", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === "success") {
        setEvents(data.data);
      } else {
        console.error("Error in response data:", data);
      }
    } catch (error) {
      console.error("Fetch error:", error.message);
    }
  };

  const fetchScans = async () => {
    if (!selectedEvent) return; // Don't fetch if no event selected
    
    setLoading(true);
    try {
      let queryParams = new URLSearchParams();
      queryParams.append('eventId', selectedEvent);
      if (selectedDate) {
        queryParams.append('date', selectedDate);
      }

      const response = await fetch(`/api/get/scans?${queryParams.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === "success") {
        setScanList(data.data);
        setDailySummary(data.summary || {});
      } else {
        console.error("Error in response data:", data);
      }
    } catch (error) {
      console.error("Fetch error:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = (event) => {
    setSelectedEvent(event.target.value);
    if (!event.target.value) {
      setScanList([]);
      setDailySummary({});
    }
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleClearFilters = () => {
    setSelectedEvent("");
    setSelectedDate("");
    setScanList([]);
    setDailySummary({});
  };

  const columns = [
    { 
      field: "id", 
      headerName: "Sr.No.", 
      width: 80,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: "mobileNumber",
      headerName: "Mobile Number",
      width: 150,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: "eventName",
      headerName: "Event Name",
      width: 250,
      headerAlign: 'center',
      align: 'left'
    },
    {
      field: "scannedAt",
      headerName: "Scanned At",
      width: 180,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: "creation",
      headerName: "Creation Date",
      width: 180,
      headerAlign: 'center',
      align: 'center'
    },
  ];

  const rows = scanList.map((item, index) => ({
    id: index + 1,
    mobileNumber: item?.mobileNumber,
    eventName: item?.eventName || "N/A",
    scannedAt: item?.scannedAt && formatDateTime(item.scannedAt),
    creation: item?.createdAt && formatDateTime(item.createdAt),
  }));

  // Calculate statistics
  const totalScans = scanList.length;
  const uniqueNumbers = new Set(scanList.map(scan => scan.mobileNumber)).size;
  const daysWithScans = Object.keys(dailySummary).length;

  return (
    <AdminLayout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Scan Results
        </Typography>

        {/* Filter Section */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Event *</InputLabel>
                <Select
                  value={selectedEvent}
                  onChange={handleEventChange}
                  label="Select Event *"
                >
                  <MenuItem value="">
                    <em>-- Select an Event --</em>
                  </MenuItem>
                  {events.map((event) => (
                    <MenuItem key={event._id} value={event._id}>
                      {event.name || event.eventName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Select Date (Optional)"
                value={selectedDate}
                onChange={handleDateChange}
                disabled={!selectedEvent}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid> */}
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                disabled={!selectedEvent && !selectedDate}
                sx={{ height: '40px' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Statistics Cards - Only show when event is selected */}
        {selectedEvent && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Card elevation={2}>
                <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Scans
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {totalScans}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={2}>
                <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Unique Numbers
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {uniqueNumbers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={2}>
                <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Days with Scans
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {daysWithScans}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Data Grid */}
        {!selectedEvent ? (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              backgroundColor: '#f9f9f9'
            }}
          >
            <Typography variant="h6" color="textSecondary">
              Please select an event to view scan results
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={2}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              autoHeight
              loading={loading}
              disableRowSelectionOnClick
              slots={{
                toolbar: GridToolbar,
                noRowsOverlay: () => (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    p: 3
                  }}>
                    <Typography color="textSecondary">
                      No scans found for this event
                    </Typography>
                  </Box>
                ),
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f9f9f9',
                },
              }}
            />
          </Paper>
        )}
      </Box>
    </AdminLayout>
  );
};

export default ScannerList;